import type { ConceptDefinitionDTO, RiskModelExposureLevels, TimeHorizonEnum } from "@eu.mdotm.api/smart-workspaces";
import { ClickableDiv, Icon, Text, textDataAttr } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	HierarchicalSelect,
	type HierarchicalSelectTriggerProps,
	Select,
	type SelectTriggerProps,
} from "@mdotm/shadcn/mdotm/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { OrderedSet } from "immutable";
import { forwardRef, type ReactNode } from "react";
import type { ConceptParameterForm } from "../chart-type";
import { relativenessWord } from "../representation-mode";
import { isConceptCustomExposureTag } from "../repository";
import { createRiskModelOptions } from "./ChainableElement";
import { chartStepOrder } from "./ConceptChain";
import {
	chainParameters,
	changePin,
	changesOnly,
	combinationsUnder,
	freeLevelsWithValues,
	inlineChainAddableLevels,
	inlineChainWords,
	inlineRowNamesConcept,
	isLiveWord,
	parametersFromPins,
	pinsFromParameters,
	relativenessWordLabel,
	removePin,
	riskModelConceptId,
	tagExposureOptions,
	type InlineConceptChainData,
	type InlineConceptPatch,
	type InlineConceptSeries,
	type InlineWord,
	type RiskModelLevel,
	type RiskModelPin,
	type TagOptionValue,
} from "./inline-chain-rules";

/**
 * The metric's own parameters as header controls: the time horizon, the tag option and the risk model's
 * pinned levels are each the panel select behind that word of the generated name. The name's other words
 * — the concept's label, and the "Rel to" pair — are drawn as text, because the metric's type and what it
 * is compared against are the Edit Chart panel's to change. Confidence is not in the name at all.
 *
 * Two renderings of the same steps: {@link InlineConceptChainTitle} where the chart's label is the
 * generated one, {@link InlineConceptChainRow} as a compact row under a title the user typed.
 *
 * Pure: the components take the concept, its form, the series' current fields and whatever option lists
 * the caller has loaded, and call `onChange` with a patch; fetching and persisting are the caller's.
 */

export type InlineConceptChainProps = {
	concept: ConceptDefinitionDTO;
	/** Which values the concept accepts for the chart type being described — `timeSeriesFormOf`. */
	form: ConceptParameterForm;
	series: InlineConceptSeries;
	data: InlineConceptChainData;
	onChange(patch: InlineConceptPatch): void;
};

/** The leaf `createRiskModelOptions` builds: a level and one of its values, or `null` on a category node. */
type RiskModelLeafValue = ReturnType<typeof createRiskModelOptions>[number]["value"];

export type TitleTriggerExtraProps = { label: string };

/**
 * A word in the chart's own title: the title's type, a hover pill, and no chevron of its own.
 *
 * Typed for both libraries' trigger contracts so one component serves `Select` and `HierarchicalSelect`;
 * `isMulti` is `Select`'s alone and would reach the `<button>` as an unknown attribute.
 */
export const InlineTitleTrigger = forwardRef<
	HTMLButtonElement,
	HierarchicalSelectTriggerProps & Partial<Pick<SelectTriggerProps, "isMulti" | "onKeyDown">> & TitleTriggerExtraProps
>(function InlineTitleTrigger(
	{
		open: _open,
		triggerLabel: _triggerLabel,
		isEmpty: _isEmpty,
		isMulti: _isMulti,
		disabled,
		invalid: _invalid,
		size: _size,
		enableClear: _enableClear,
		onClear,
		label,
		...rest
	},
	ref,
) {
	return (
		<button
			ref={ref}
			type="button"
			disabled={disabled}
			className="group -mx-1 inline-flex max-w-full items-center gap-1 rounded-[4px] px-1.5 hover:bg-(--palette-N50) focus-visible:bg-(--palette-N50) focus-visible:outline-none data-[state=open]:bg-(--palette-N50)"
			style={{ color: themeCSSVars.palette_N500 }}
			{...textDataAttr("Body/M/Bold")}
			{...rest}
		>
			<span className="truncate">{label}</span>
			{onClear && (
				<ClickableDiv
					title="Clear"
					className="inline-flex shrink-0 text-(--palette-N400) opacity-0 group-hover:opacity-100 group-data-[state=open]:opacity-100 focus-within:opacity-100 hover:text-(--palette-N800)"
					onClick={(e) => {
						e.stopPropagation();
						onClear();
					}}
				>
					<Icon icon="Close" size={12} />
				</ClickableDiv>
			)}
		</button>
	);
});

/** The breadcrumb's own chevron, turned a quarter — a level chain reads as a path, not as a fraction. */
export function InlineTitleSeparator(): React.JSX.Element {
	return (
		<Icon
			icon="Down"
			size={12}
			color={themeCSSVars.palette_N400}
			style={{ transform: "rotateZ(-90deg)" }}
			aria-hidden
		/>
	);
}

/**
 * The risk-model step: one picker per pin, plus the trailing one that adds a level while any free level
 * still has values. Unlike the panel's `SelectConceptExposureRiskModel`, a change keeps every later pin
 * that still has rows under the new one (`changePin`).
 */
export function InlineRiskModelSlots(props: {
	combinations: ReadonlyArray<RiskModelExposureLevels>;
	pins: ReadonlyArray<RiskModelPin>;
	onPinsChange(pins: RiskModelPin[]): void;
	variant: "title" | "row";
	/** Off where the caller offers the free levels from a select of its own. Default on. */
	withAddSelect?: boolean;
}): React.JSX.Element {
	const { combinations, pins, onPinsChange, variant, withAddSelect = true } = props;
	const rowsUnderPins = combinationsUnder(combinations, pins);
	const addable = freeLevelsWithValues(rowsUnderPins, pins);

	return (
		<>
			<ForEach collection={pins} keyProvider={(pin) => pin.key}>
				{(pin, index) => {
					const rowsBefore = combinationsUnder(combinations, pins.slice(0, index));
					// The slot's own level first — swapping the value in place — then the levels pinned nowhere,
					// which is how a level's *category* is swapped without disturbing the ones before it.
					const options = createRiskModelOptions(
						rowsBefore,
						OrderedSet<RiskModelLevel>([pin.key, ...freeLevelsWithValues(rowsBefore, pins)]),
						[],
					);
					// A valueless answer is the clear ×, and also a category node that turned out to have no
					// children; both mean "drop this level".
					const onChange = (next: RiskModelLeafValue | null) =>
						next?.value
							? onPinsChange(changePin(combinations, pins, index, { key: next.key, value: next.value }))
							: onPinsChange(removePin(pins, index));

					return variant === "title" ? (
						<>
							{index > 0 && <InlineTitleSeparator />}
							<HierarchicalSelect<RiskModelLeafValue, TitleTriggerExtraProps>
								enableClear
								enableSearch
								value={pin}
								options={options}
								onChange={onChange}
								Trigger={InlineTitleTrigger}
								extraTriggerProps={{ label: pin.value }}
							/>
						</>
					) : (
						<HierarchicalSelect
							enableClear
							enableSearch
							size="x-small"
							// Sized to `Level / Value` and kept there: a flex item that may shrink truncates the
							// value — the half that is worth reading — long before the row would wrap.
							className="w-auto max-w-full shrink-0"
							value={pin}
							options={options}
							onChange={onChange}
						/>
					);
				}}
			</ForEach>
			{withAddSelect && addable.length > 0 && (
				<>
					{variant === "title" && <InlineTitleSeparator />}
					<HierarchicalSelect
						enableSearch
						size="x-small"
						className="w-auto shrink-0"
						value={null}
						options={createRiskModelOptions(rowsUnderPins, OrderedSet(addable), [])}
						onChange={(next) => next?.value && onPinsChange([...pins, { key: next.key, value: next.value }])}
					/>
				</>
			)}
		</>
	);
}

/** The static rendering of a word the chain cannot offer a list for. */
function staticWordLabel(word: InlineWord, concept: ConceptDefinitionDTO, series: InlineConceptSeries): string {
	switch (word.kind) {
		case "conceptLabel":
			return concept.defaultLabel ?? "";
		case "timeHorizon":
			return series.timeHorizon ?? "";
		case "metricType":
			return relativenessWord(series.representationMode);
		case "entity":
			return series.relativeness ? relativenessWordLabel(series.relativeness) : "";
		case "tagOption":
			return chainParameters(series)?.tagExposureOption ?? "";
		case "riskModel":
			return pinsFromParameters(chainParameters(series))
				.map((pin) => pin.value)
				.join(" / ");
	}
}

/** One word of the title: the select behind it, or the plain text when there is no list to offer. */
function InlineChainTitleWord(props: InlineConceptChainProps & { word: InlineWord }): ReactNode {
	const { word, concept, form, series, data, onChange } = props;

	if (!isLiveWord(word)) {
		return (
			<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
				{staticWordLabel(word, concept, series)}
			</Text>
		);
	}

	switch (word.kind) {
		case "timeHorizon":
			return (
				<Select<TimeHorizonEnum, TitleTriggerExtraProps>
					value={series.timeHorizon ?? null}
					options={(form.allowedTimeHorizons ?? []).map((horizon) => ({ value: horizon, label: horizon }))}
					onChange={(horizon) => horizon && onChange({ timeHorizon: horizon })}
					Trigger={InlineTitleTrigger}
					extraTriggerProps={{ label: series.timeHorizon ?? "" }}
				/>
			);
		case "tagOption":
			return (
				<HierarchicalSelect<TagOptionValue, TitleTriggerExtraProps>
					enableSearch
					value={{ id: series.conceptId, opt: chainParameters(series)?.tagExposureOption }}
					options={tagExposureOptions(data.tagConcepts)}
					onChange={(next) =>
						next?.opt && onChange({ conceptId: next.id, parameters: { tagExposureOption: next.opt } })
					}
					Trigger={InlineTitleTrigger}
					extraTriggerProps={{ label: staticWordLabel(word, concept, series) }}
				/>
			);
		case "riskModel":
			return (
				<InlineRiskModelSlots
					variant="title"
					withAddSelect={false}
					combinations={data.combinations ?? []}
					pins={pinsFromParameters(chainParameters(series))}
					onPinsChange={(next) => onChange({ parameters: parametersFromPins(next) })}
				/>
			);
	}
}

/** The trailing "add a level" affordance, for a risk model whose pins do not yet fill the chain. */
function InlineChainAddSelect(props: Omit<InlineConceptChainProps, "form">): ReactNode {
	const { concept, series, data, onChange } = props;
	const addable = inlineChainAddableLevels(concept, series, data);
	if (addable.length === 0) {
		return null;
	}

	const pins = pinsFromParameters(chainParameters(series));
	return (
		<HierarchicalSelect<RiskModelLeafValue>
			enableSearch
			size="x-small"
			className="w-auto shrink-0"
			value={null}
			options={createRiskModelOptions(combinationsUnder(data.combinations ?? [], pins), OrderedSet(addable), [])}
			onChange={(pick) =>
				pick?.value && onChange({ parameters: parametersFromPins([...pins, { key: pick.key, value: pick.value }]) })
			}
		/>
	);
}

/** The generated title, made the control. No quotes — those are the static title's. */
export function InlineConceptChainTitle(props: InlineConceptChainProps): React.JSX.Element {
	const words = inlineChainWords(props.concept, props.form, props.series, props.data);
	const hasAddSelect = inlineChainAddableLevels(props.concept, props.series, props.data).length > 0;
	const guarded = { ...props, onChange: changesOnly(props.series, props.onChange) };

	return (
		<span className="inline-flex min-w-0 flex-wrap items-center gap-1">
			<ForEach collection={words} keyProvider={(word) => word.kind}>
				{(word, index) => (
					<>
						{index > 0 && <InlineTitleSeparator />}
						<InlineChainTitleWord {...guarded} word={word} />
					</>
				)}
			</ForEach>
			{hasAddSelect && (
				<>
					{words.length > 0 && <InlineTitleSeparator />}
					<InlineChainAddSelect {...guarded} />
				</>
			)}
		</span>
	);
}

/** The metric's own parameter steps, compact, for under a title the user typed. */
export function InlineConceptChainRow(props: InlineConceptChainProps): React.JSX.Element | null {
	const { concept, form, series, data } = props;
	const onChange = changesOnly(series, props.onChange);
	const parameters = chainParameters(series);
	const compact = { size: "x-small", className: "w-auto max-w-full shrink-0" } as const;

	const steps = chartStepOrder.flatMap((step): ReactNode[] => {
		switch (step) {
			case "tagExposure":
				if (!isConceptCustomExposureTag(series.conceptId) || (data.tagConcepts?.length ?? 0) === 0) {
					return [];
				}
				return [
					<HierarchicalSelect<TagOptionValue>
						key="step-tag-exposure"
						{...compact}
						enableSearch
						placeholder="Select tag"
						value={{ id: series.conceptId, opt: parameters?.tagExposureOption }}
						options={tagExposureOptions(data.tagConcepts)}
						onChange={(next) =>
							next?.opt && onChange({ conceptId: next.id, parameters: { tagExposureOption: next.opt } })
						}
					/>,
				];
			case "riskModelExposure":
				if (concept.id !== riskModelConceptId || !data.combinations) {
					return [];
				}
				return [
					<InlineRiskModelSlots
						key="step-risk-model-exposure"
						variant="row"
						combinations={data.combinations}
						pins={pinsFromParameters(parameters)}
						onPinsChange={(next) => onChange({ parameters: parametersFromPins(next) })}
					/>,
				];
			case "timeHorizon":
				if ((form.allowedTimeHorizons?.length ?? 0) === 0) {
					return [];
				}
				return [
					<Select<TimeHorizonEnum>
						key="step-time-horizon"
						{...compact}
						value={series.timeHorizon ?? null}
						options={(form.allowedTimeHorizons ?? []).map((horizon) => ({ value: horizon, label: horizon }))}
						onChange={(horizon) => horizon && onChange({ timeHorizon: horizon })}
					/>,
				];
			default:
				return [];
		}
	});

	if (steps.length === 0) {
		return null;
	}
	return (
		<div className="flex flex-wrap items-center gap-2">
			{inlineRowNamesConcept(concept, form, series, data) && (
				<Text type="Body/S/Bold" color={themeCSSVars.palette_N500}>
					{concept.defaultLabel ?? ""}
				</Text>
			)}
			{steps}
		</div>
	);
}
